import React, { useCallback, useEffect, useRef, useState } from 'react';
import 'twin.macro';
import { Input, RequiredAstrix } from '../form';
import { Button } from '../button';
import { Icon } from '../icon';
import { LoaderIcon } from 'icons';
import { Notification } from '../notifications';
import { registerDumpertUser } from 'services/user';
import { gaEvent } from 'services/ga';
import { Small } from '../text';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { settings } from '../../settings';
import { DateInput } from '../form/DateInput';

export const INVALID_DATE = 'Dag moet tussen 1 en 31 liggen';
export const INVALID_MONTH = 'Maand moet tussen 1 en 12 liggen';
export const INVALID_YEAR = 'Jaar moet tussen 1900 en 2022 liggen';

export const UNKNOWN_ERROR = 'unknown error';

const genderOptions = [
  { label: 'Man', value: 'M' },
  { label: 'Vrouw', value: 'V' },
  { label: 'Iets', value: 'na' },
];

export const RegisterForm = (props) => (
  <GoogleReCaptchaProvider reCaptchaKey={settings.RECAPTCHA_SITE_KEY}>
    <RegisterFormWithCaptcha {...props} />
  </GoogleReCaptchaProvider>
);

const RegisterFormWithCaptcha = ({ focused = false }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [livesinnl, setLivesinnl] = useState(true);
  const [gender, setGender] = useState('');
  const [birthdate, setBirthdate] = useState({ day: '', month: '', year: '' });
  const [pending, setPending] = useState(false);
  const [notification, setNotification] = useState({});
  const formRef = useRef();
  const inputRef = useRef();

  const { executeRecaptcha } = useGoogleReCaptcha();

  useEffect(() => {
    focused && inputRef.current.focus();
  }, []);

  const submit = useCallback(
    async (e) => {
      e.preventDefault();
      setNotification({});
      setPending(true);
      let recaptcha = '';
      if (executeRecaptcha) {
        recaptcha = await executeRecaptcha('register');
      }

      try {
        await registerDumpertUser({
          birthdate: `${birthdate.month}-${birthdate.day}-${birthdate.year}`,
          email,
          gender,
          livesinnl,
          password,
          username,
          recaptcha,
        });

        setNotification({
          state: 'success',
          message:
            'Je bent succesvol geregistreerd, er is een bevestigingmail naar je email toegestuurd.',
        });
        setUsername('');
        setPassword('');
        setEmail('');
        setLivesinnl(false);
        setBirthdate({ day: '', month: '', year: '' });
        setGender('');
      } catch (err) {
        if (err.response && err.response.data && err.response.data.error) {
          setNotification({
            state: 'error',
            message: err.response.data.message,
          });
        } else {
          setNotification({ state: 'error', message: UNKNOWN_ERROR });
        }
      } finally {
        gaEvent('dmp_user', 'register');
        setPending(false);
      }
    },
    [birthdate, email, gender, livesinnl, password, username],
  );

  return (
    <>
      {notification.state && (
        <Notification
          message={notification.message}
          style={notification.state}
          isHtml
          onClick={() => setNotification({})}
          tw='mb-4'
        />
      )}

      <form tw='text-left' onSubmit={submit} ref={formRef}>
        <Input
          ref={inputRef}
          label='Gebruikersnaam'
          id='register-username'
          placeholder='Gebruikersnaam'
          value={username}
          maxLength='20'
          requiredField
          hasInlineAsterisk
          onChange={(e) => setUsername(e.target.value)}
        />
        <Input
          label='E-mail'
          id='register-email'
          placeholder='E-mail'
          type='email'
          value={email}
          requiredField
          hasInlineAsterisk
          onChange={(e) => setEmail(e.target.value)}
        />
        <Input
          label='Wachtwoord'
          id='register-password'
          type='password'
          placeholder='Wachtwoord'
          value={password}
          maxLength='20'
          requiredField
          hasInlineAsterisk
          onChange={(e) => setPassword(e.target.value)}
        />
        <DateInput birthDate={birthdate} setBirthdate={setBirthdate} />
        <div tw='relative w-full'>
          <Small text='Geslacht' tw='font-bold' />
          <RequiredAstrix hasInlineAsterisk />
        </div>
        <div tw='flex gap-4 mb-4'>
          {genderOptions.map((item) => (
            <Input
              key={item.value}
              type='radio'
              label={item.label}
              value={item.value}
              checked={gender === item.value}
              name='gender'
              onChange={() => setGender(item.value)}
              tw='flex'
            />
          ))}
        </div>
        <div tw='w-full'>
          <Input
            type='checkbox'
            label='Woonachtig in Nederland'
            onChange={(e) => setLivesinnl(e.target.checked)}
            checked={livesinnl}
          />
        </div>
        <div tw='w-full text-center'>
          <Button
            label={
              !pending ? (
                'registreren'
              ) : (
                <Icon Type={LoaderIcon} tw='animate-spin h-5' color='input-back' size='small' />
              )
            }
            primary
            tw='mt-4 mx-auto'
            type='submit'
            disabled={!(username && password && email && gender && birthdate)}
          />
        </div>
      </form>
    </>
  );
};
